import React, { useMemo } from "react";
import { User, Activity, Zap, BarChart3, Gauge, Footprints, Timer, TrendingUp, ArrowLeftRight } from "lucide-react";
import { MUSCLES } from "./constants";
import TrainabilityBadge from "./TrainabilityBadge";
import ProgressBar from "./ProgressBar";
import MuscleTrainabilityBar from "./MuscleTrainabilityBar";
import MetricTooltip from "./MetricTooltip";
import MetricItem from "./MetricItem";

const r2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
};

const fmt = (v, suffix = "", fallback = "—") => {
  const n = r2(v);
  return n === null ? fallback : `${n}${suffix}`;
};

const fmtInt = (v, fallback = "—") => {
  const n = Number(v);
  return Number.isFinite(n) ? String(Math.round(n)) : fallback;
};


const sumNums = (arr) =>
  Array.isArray(arr) ? arr.reduce((a, b) => a + (Number.isFinite(+b) ? +b : 0), 0) : null;



const clamp01 = (n) => Math.max(0, Math.min(1, n));
const clamp10 = (n) => Math.max(0, Math.min(10, n));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const toScore10 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;

  // ancien format 0-1
  if (n >= 0 && n <= 1.5) return clamp(n * 10, 0, 10);

  // format percent (0-100)
  if (n > 10 && n <= 100) return clamp(n / 10, 0, 10);

  // cas bug: 6500 => probablement percent *100
  if (n > 100 && n <= 10000) return clamp(n / 1000, 0, 10);

  // format déjà /10
  return clamp(n, 0, 10);
};

const getBand = (score10) => {
  if (score10 < 4) return "low";
  if (score10 < 6) return "mid";
  return "high";
};

// status: "underload" | "overload" | null
const getTrainabilityTone = (score10, status) => {
  const band = getBand(score10);

  if (band === "high") return "green";    // >= 6: optimal
  if (band === "mid") return "amber";     // 4-6: medium
  return "red";                             // < 4: at risk
};

const toneToTextClass = (tone, isDarkMode) => {
  switch (tone) {
    case "red": return isDarkMode ? "text-red-400" : "text-red-600";
    case "amber": return isDarkMode ? "text-amber-400" : "text-amber-600";
    case "green": return isDarkMode ? "text-emerald-400" : "text-emerald-600";
    default: return isDarkMode ? "text-gray-300" : "text-gray-600";
  }
};


const toneToPillClass = (tone, isDarkMode) => {
  const base = "inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold";
  const dark = isDarkMode;

  switch (tone) {
    case "red":
      return `${base} ${dark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700"}`;
    case "amber":
      return `${base} ${dark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"}`;
    case "green":
      return `${base} ${dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`;
    default:
      return `${base} ${dark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-700"}`;
  }
};

const statusLabel = (score10, status) => {
  const v = Number(score10);

  if (!Number.isFinite(v)) return "N/A";
  if (v < 4) return "At Risk";
  if (v < 6) return "Medium";
  return "Optimal";
};



const PlayerDetailPanel = ({ player, isDarkMode }) => {
  // Build muscle data from player sessions
  const muscleData = useMemo(() => {
  if (!player?.sessions?.length) return null;
  const lastSession = player.sessions[player.sessions.length - 1];
  if (!lastSession) return null;

  return {
    quadriceps: {
      right: toScore10(lastSession.trainability_quadriceps_r),
      left:  toScore10(lastSession.trainability_quadriceps_l),
    },
    hamstring: {
      right: toScore10(lastSession.trainability_hamstring_r),
      left:  toScore10(lastSession.trainability_hamstring_l),
    },
    adductors: {
      right: toScore10(lastSession.trainability_adductors_r),
      left:  toScore10(lastSession.trainability_adductors_l),
    },
    calf: {
      right: toScore10(lastSession.trainability_calf_r),
      left:  toScore10(lastSession.trainability_calf_l),
    },
    gluteal: {
      right: toScore10(lastSession.trainability_gluteal_r),
      left:  toScore10(lastSession.trainability_gluteal_l),
    },
    iliopsoas: {
      right: toScore10(lastSession.trainability_iliopsoas_r),
      left:  toScore10(lastSession.trainability_iliopsoas_l),
    },
  };
}, [player]);


  if (!player) {
    return (
      <div className={`flex items-center justify-center h-full min-h-[300px] ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}>
        <div className="text-center">
          <User size={40} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm opacity-60">Select a player</p>
        </div>
      </div>
    );
  }

  // Get last session data
  const lastSession = player.sessions?.[player.sessions.length - 1];
  const accelData = lastSession?.session_acceleration_sprint;
  const perfData = lastSession?.session_performance;
  const loadStatus =
    lastSession?.trainability_level?.global ??
    player?.trainability_level?.global ??
    null;
  const score10 = Number.isFinite(player?.avg_trainability_10)
    ? player.avg_trainability_10
    : toScore10(lastSession?.trainability_index ?? 0);

  const tone = getTrainabilityTone(score10, loadStatus);
const scoreTextClass = toneToTextClass(tone, isDarkMode);
  const pillClass = toneToPillClass(tone, isDarkMode);


  return (
    <div className="space-y-5">
      {/* Header - no border */}
      <div className="flex items-center gap-4">
        {player.profile_picture ? (
          <img
            src={`data:image/jpeg;base64,${player.profile_picture}`}
            alt={`${player.first_name} ${player.last_name}`}
            className="w-14 h-14 rounded-2xl object-cover"
          />
        ) : (
          <div
            className={`
              w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold
              ${isDarkMode ? "bg-gradient-to-br from-gray-700 to-gray-800 text-gray-300" : "bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600"}
            `}
          >
            {player.first_name?.[0]}
            {player.last_name?.[0]}
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-lg font-bold">
            {player.first_name} {player.last_name}
          </h3>
          <div className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            #{player.jersey_number} · {player.position}
          </div>
        </div>
        
        <div className="text-right">
          <MetricTooltip
            isDarkMode={isDarkMode}
            content={"Trainability score (/10) based on predictive simulation. <4 not good, 4-6 medium, >6 good. Color indicates underload/overload."}
            placement="top"
            maxWidthClass="max-w-sm"
          >
            <div className="cursor-help outline-none rounded">
              <div className={`text-2xl font-bold ${scoreTextClass}`}>
                {score10.toFixed(1)}/10
              </div>

              <div className={`mt-1 ${pillClass}`}>
                {statusLabel(score10, loadStatus)}
              </div>

              <div className={`mt-1 text-[10px] uppercase tracking-wide ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Trainability
              </div>
            </div>
          </MetricTooltip>
        </div>
      </div>

      {/* Muscle Breakdown - compact grid */}
      {muscleData && (
        <div>
          <div className={`text-xs font-medium mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            <Activity size={14} className="text-violet-500" />
            <MetricTooltip
              isDarkMode={isDarkMode}
              content={"Muscle-level trainability breakdown. Shows left/right scores (0–10) derived from the last simulated session. Higher is better."}
              placement="top"
              maxWidthClass="max-w-sm"
            >
              <span className="cursor-help">Trainability by Muscle</span>
            </MetricTooltip>
          </div>

          <div className="grid grid-cols-2 gap-2 auto-rows-fr">
            {MUSCLES.map((m) => (
              <MetricTooltip
                key={m.key}
                isDarkMode={isDarkMode}
                content={`${m.label}: predicted trainability score (0–10) for left and right sides, based on the simulated microcycle workload.`}
                placement="top"
                maxWidthClass="max-w-sm"
              >
                <div className="cursor-help w-full h-full">
                  <MuscleTrainabilityBar
                    muscle={m.label}
                    right={muscleData[m.key]?.right || 0}
                    left={muscleData[m.key]?.left || 0}
                    maxValue={10}
                    isDarkMode={isDarkMode}
                  />

                </div>
              </MetricTooltip>
            ))}
          </div>
        </div>
      )}

      {/* Performance Metrics - flat design */}
      {accelData && (
        <div>
          <div className={`text-xs font-medium mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            <Zap size={14} className="text-amber-500" />
            <MetricTooltip
              isDarkMode={isDarkMode}
              content={"Key performance metrics extracted from the simulated session data. Values may vary depending on GPS source and session type."}
              placement="top"
              maxWidthClass="max-w-sm"
            >
              <span className="cursor-help">Metrics</span>
            </MetricTooltip>
          </div>

          <div className="grid grid-cols-3 gap-x-4 gap-y-2">
            {[
              {
                icon: Footprints,
                label: "Distance",
                value: (() => {
                  const km =
                    accelData.global_distance ??
                    (accelData.global?.distance_total != null ? accelData.global.distance_total / 1000 : null);
                  return fmt(km, "km");
                })(),
                tooltip: "Total distance covered during the session, converted to kilometers.",
              },
              {
                icon: Gauge,
                label: "Vmax",
                value: fmt(accelData.sprint_max ?? accelData.velocity?.max, "km/h"),
                tooltip: "Maximum speed reached during the session (km/h).",
              },
              {
                icon: Zap,
                label: "Sprints",
                value: (() => {
                  const c = sumNums(accelData.sprint_number) ?? accelData.sprint_number;
                  return fmtInt(c) ?? "—";
                })(),
                tooltip: "Total number of sprints detected during the session.",
              },
              {
                icon: TrendingUp,
                label: "Accel.",
                value: (() => {
                  const c = sumNums(accelData.acceleration_number) ?? accelData.acceleration?.count;
                  return fmtInt(c) ?? "—";
                })(),
                tooltip: "Number of high-intensity accelerations recorded during the session.",
              },
              {
                icon: ArrowLeftRight,
                label: "COD",
                value: fmt(accelData.COD_number, ""),
                tooltip: "Number of changes of direction detected during the session.",
              },
              {
                icon: Timer,
                label: "Max Accel",
                value: fmt(accelData.acceleration_max ?? accelData.acceleration?.max_value, "m/s²"),
                tooltip: "Peak acceleration value recorded during the session (m/s²).",
              },
            ].map((m, idx) => (
              <MetricItem
                key={idx}
                icon={m.icon}
                label={m.label}
                value={m.value}
                tooltip={m.tooltip}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        </div>
      )}

      {/* Force-Velocity Profile - minimal */}
      {perfData && (
        <div>
          <div className={`text-xs font-medium mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            <BarChart3 size={14} className="text-indigo-500" />
            <MetricTooltip
              isDarkMode={isDarkMode}
              content={"Force-velocity profile derived from sprint data. Shows maximum force, power, and velocity for vertical and horizontal planes."}
              placement="top"
              maxWidthClass="max-w-sm"
            >
              <span className="cursor-help">Force-Velocity Profile</span>
            </MetricTooltip>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] font-medium text-indigo-500 mb-1.5">Vertical</div>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between">
                  <MetricTooltip isDarkMode={isDarkMode} content={"Maximum vertical force produced (Newtons)."}>
                    <span className={`cursor-help ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>F max</span>
                  </MetricTooltip>
                  <span className="font-medium">{fmt(perfData.force_velocity_profile?.vertical?.max_force, 'N')}</span>
                </div>
                <div className="flex justify-between">
                  <MetricTooltip isDarkMode={isDarkMode} content={"Maximum vertical power output (Watts)."}>
                    <span className={`cursor-help ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>P max</span>
                  </MetricTooltip>
                  <span className="font-medium">{fmt(perfData.force_velocity_profile?.vertical?.max_power, 'W')}</span>
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] font-medium text-cyan-500 mb-1.5">Horizontal</div>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between">
                  <MetricTooltip isDarkMode={isDarkMode} content={"Maximum horizontal force produced (Newtons)."}>
                    <span className={`cursor-help ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>F max</span>
                  </MetricTooltip>
                  <span className="font-medium">{fmt(perfData.force_velocity_profile?.horizontal?.max_force, 'N')}</span>
                </div>
                <div className="flex justify-between">
                  <MetricTooltip isDarkMode={isDarkMode} content={"Maximum horizontal velocity reached (km/h)."}>
                    <span className={`cursor-help ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>V max</span>
                  </MetricTooltip>
                  <span className="font-medium">{fmt(perfData.force_velocity_profile?.horizontal?.max_velocity, 'km/h')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDetailPanel;
